import { jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useCallback, useMemo, useImperativeHandle } from 'react';
import { pick, omit } from '../../utils/index.js';
import useMount from '../../utils/useMount.js';
import { ViewContext } from '../contexts.js';
import useApplyCenterOfRotation from '../modules/useApplyCenterOfRotation.js';
import { useInteractorStyle, useInteractorStyleManipulatorSettings } from '../modules/useInteractorStyle.js';
import useViewEvents, { ViewEvents } from '../modules/useViewEvents.js';
import OpenGLRenderWindow from '../OpenGLRenderWindow.js';
import Renderer from '../Renderer.js';
import RenderWindow from '../RenderWindow.js';
import { viewMountedEvent } from './events.js';
import { DefaultProps } from './view-shared.js';

/**
 * A standalone View (not within a MultiViewRoot).
 */
const SingleView = forwardRef(function SingleView(props, fwdRef) {
    // view API just exposes the render window + renderer
    const openGLRenderWindowRef = useRef(null);
    const renderWindowRef = useRef(null);
    const rendererRef = useRef(null);
    const rendererProps = pick(props, 'background', 'interactive', 'camera', 'autoResetCamera');
    const openGLRenderWindowProps = omit(props, ...[...Object.keys(rendererProps)]);
    // --- interactor style --- //
    const getInteractor = useCallback(() => { var _a, _b; return (_b = (_a = renderWindowRef.current) === null || _a === void 0 ? void 0 : _a.getInteractor()) !== null && _b !== void 0 ? _b : null; }, []);
    const [getInteractorStyle, setInteractorStyle, isExternalStyle] = useInteractorStyle(getInteractor);
    useMount(() => {
        var _a;
        (_a = getInteractor()) === null || _a === void 0 ? void 0 : _a.setInteractorStyle(getInteractorStyle());
    });
    const { interactorSettings = DefaultProps.interactorSettings, autoCenterOfRotation = DefaultProps.autoCenterOfRotation, } = props;
    useInteractorStyleManipulatorSettings(getInteractorStyle, isExternalStyle, interactorSettings);
    useApplyCenterOfRotation(rendererRef, getInteractorStyle, autoCenterOfRotation);
    // --- events --- //
    const { rootListeners, registerEventListener } = useViewEvents();
    // --- api --- //
    let mounted = false;
    useMount(() => {
        mounted = true;
        viewMountedEvent.trigger();
    });
    const api = useMemo(() => ({
        isInMultiViewRoot: () => false,
        isMounted: () => mounted,
        getViewContainer: () => { var _a, _b; return (_b = (_a = openGLRenderWindowRef.current) === null || _a === void 0 ? void 0 : _a.getContainer()) !== null && _b !== void 0 ? _b : null; },
        getOpenGLRenderWindow: () => openGLRenderWindowRef.current,
        getRenderWindow: () => renderWindowRef.current,
        getRenderer: () => rendererRef.current,
        getInteractorStyle: () => getInteractorStyle(),
        setInteractorStyle: (style) => setInteractorStyle(style),
        requestRender: () => { var _a; return (_a = rendererRef.current) === null || _a === void 0 ? void 0 : _a.requestRender(); },
        getCamera: () => { var _a, _b; return (_b = (_a = rendererRef.current) === null || _a === void 0 ? void 0 : _a.get().getActiveCamera()) !== null && _b !== void 0 ? _b : null; },
        resetCamera: (boundsToUse) => { var _a; return (_a = rendererRef.current) === null || _a === void 0 ? void 0 : _a.resetCamera(boundsToUse); },
    }), [mounted, getInteractorStyle, setInteractorStyle]);
    useImperativeHandle(fwdRef, () => api);
    return (jsx(ViewContext.Provider, Object.assign({ value: api }, { children: jsx(ViewEvents.Provider, Object.assign({ value: registerEventListener }, { children: jsx(OpenGLRenderWindow, Object.assign({}, openGLRenderWindowProps, rootListeners, { ref: openGLRenderWindowRef }, { children: jsx(RenderWindow, Object.assign({ ref: renderWindowRef }, { children: jsx(Renderer, Object.assign({}, rendererProps, { ref: rendererRef }, { children: props.children })) })) })) })) })));
});

export { SingleView as default };
